import { action, observable, runInAction } from 'mobx';
import isEqual from 'lodash/isEqual';
import uniqWith from 'lodash/uniqWith';
import { ErrorInterface } from 'modules/core/api/globalApi';
import controller from './controller';

/** Элемент списка сервисов для селекторов (ветка partnerId маппится к этому виду, как в Redux-экшене). */
export interface ServiceItemInterface {
  id: number,
  name?: string,
  stationary?: boolean,
  useInsurance?: boolean,
  // HEX-цвет плитки услуги из справочника «Сервисы» (поле «Цвет», /v1/services).
  // Может отсутствовать или быть пустым — потребители обязаны иметь fallback.
  color?: string,
}

export interface ServicesStore {
  // полный список сервисов ('all') или сервисы конкретного партнёра (по partnerId)
  services: ServiceItemInterface[],
  // ключ кэша = partnerId последнего успешного запроса или 'all'; null — данных ещё не было
  servicesRequest: string | null,
  // «свои» сервисы авторизованного партнёра/пользователя (ветки onlyPartnerService селектора)
  partnerServices: ServiceItemInterface[],
  // какая ветка заполнила partnerServices; null — данных ещё не было
  partnerServicesRequest: 'partner' | 'user' | null,

  loadingServices: boolean,
  loadingPartnerServices: boolean,

  error: ErrorInterface,
}

const initialValues: ServicesStore = {
  services: [],
  servicesRequest: null,
  partnerServices: [],
  partnerServicesRequest: null,

  loadingServices: false,
  loadingPartnerServices: false,

  error: null,
};

export const servicesStore = observable(initialValues);

// запрос в полёте (не обзервабл: на рендер не влияет) — single-flight на дублирующие mount'ы
let servicesInFlight: string | null = null;
let partnerServicesInFlight: 'partner' | 'user' | null = null;

export const servicesActions = {

  handleError: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      servicesStore.error = {
        message: errorData?.message,
        errorCode: errorData?.errorCode,
        timestamp: errorData?.timestamp,
        status: response?.status,
        connectError: response?.connectError,
        isError: response?.error,
      };
      throw errorData;
    }
  }),

  async clearError() {
    servicesStore.error = null;
  },

  // GET /v1/services (полный список) или GET /v1/service_partner/partner/{id} (сервисы партнёра, с маппингом)
  async getServices(partnerId?: string) {
    const requestKey = partnerId || 'all';
    if (isEqual(requestKey, servicesStore.servicesRequest) || isEqual(requestKey, servicesInFlight)) {
      return;
    }
    servicesInFlight = requestKey;
    runInAction(() => {
      // чистим синхронно, чтобы не мигать списком другого партнёра при смене partnerId
      servicesStore.services = [];
      servicesStore.loadingServices = true;
    });
    try {
      const response = partnerId
        ? await controller.getServicePartner({ partnerId })
        : await controller.getServices();

      servicesActions.handleError(response);
      runInAction(() => {
        // ключ фиксируем только при успехе: при ошибке следующий mount повторит запрос
        servicesStore.servicesRequest = requestKey;
        servicesStore.services = partnerId
          ? uniqWith(response?.data?.map(v => {
            return {
              id: v.serviceId,
              name: v.serviceName,
              stationary: v?.stationary,
              useInsurance: v?.useInsurance,
              color: v?.color,
            };
          }) ?? [], isEqual)
          : response?.data ?? [];
      });
    } catch (err) {
      this.handleError(err);
    } finally {
      servicesInFlight = null;
      runInAction(() => {
        servicesStore.loadingServices = false;
      });
    }
  },

  // GET /v1/services/partner — сервисы партнёра авторизованного пользователя.
  // Гейт перенесён 1:1 из Redux (actions.js): кэш на сессию, пустой список кэшем не считается.
  async getAuthenticatedPartnersServices() {
    if ((servicesStore.partnerServicesRequest === 'partner' && servicesStore.partnerServices.length > 0)
      || partnerServicesInFlight === 'partner'
    ) {
      return;
    }
    partnerServicesInFlight = 'partner';
    // старый список не чистим: запрос всегда по одним и тем же params, мигать пустым незачем
    runInAction(() => {
      servicesStore.loadingPartnerServices = true;
    });
    try {
      const response = await controller.getAuthenticatedPartnersServices();

      servicesActions.handleError(response);
      runInAction(() => {
        servicesStore.partnerServicesRequest = 'partner';
        servicesStore.partnerServices = response?.data ?? [];
      });
    } catch (err) {
      this.handleError(err);
    } finally {
      partnerServicesInFlight = null;
      runInAction(() => {
        servicesStore.loadingPartnerServices = false;
      });
    }
  },

  // GET /v1/services/user — сервисы самого пользователя. Как и в Redux, без кэша: каждый mount перезапрашивает.
  async getAuthenticatedUserServices() {
    if (partnerServicesInFlight === 'user') {
      return;
    }
    partnerServicesInFlight = 'user';
    runInAction(() => {
      servicesStore.loadingPartnerServices = true;
    });
    try {
      const response = await controller.getAuthenticatedUserServices();

      servicesActions.handleError(response);
      runInAction(() => {
        servicesStore.partnerServicesRequest = 'user';
        servicesStore.partnerServices = response?.data ?? [];
      });
    } catch (err) {
      this.handleError(err);
    } finally {
      partnerServicesInFlight = null;
      runInAction(() => {
        servicesStore.loadingPartnerServices = false;
      });
    }
  },

  clearServices: action(() => {
    servicesStore.services = [];
    servicesStore.servicesRequest = null;
    servicesStore.partnerServices = [];
    servicesStore.partnerServicesRequest = null;
    servicesStore.error = null;
  }),

};
